import axios from 'axios';
import { config } from '../config';

const API_URL = config.API_BASE_URL;

export type InactivityUnit = 'minutes' | 'hours' | 'days';

export interface FollowUpRuleDTO {
    id: string;
    name: string;
    prompt: string;
    content: string;
    minInactivityMinutes: number;
    inactivityValue: number;
    inactivityUnit: InactivityUnit;
    retroactiveDays: number;
    dailyLeadCap: number;
    aiAgentId?: number | null;
    enabled?: boolean;
    priority?: number;
    triggerType?: string;
    triggerStage?: string | null;
    /** Gatilho "column": coluna do Kanban do CRM. */
    triggerColumnId?: number | null;
    appointmentTypeName?: string | null;
    appointmentOutcome?: string | null;
    afterHours?: number | null;
    cooldownHours?: number;
    templateKey?: string | null;
    structuredJson?: string | null;
}

export interface FollowUpSettingsDTO {
    sectorId: number;
    enabled: boolean;
    rules: FollowUpRuleDTO[];
    effectiveTimezone?: string;
}

export interface SaveFollowUpRequest {
    enabled: boolean;
    rules: FollowUpRuleDTO[];
}

export interface DispatchDecision {
    contactId: number;
    number: string;
    decision: 'fire' | 'skip' | 'cooldown' | 'error';
    ruleId?: string;
    ruleName?: string;
    reason?: string;
    content?: string;
    inactivityMinutes: number;
    tokensIn: number;
    tokensOut: number;
}

export interface DispatchResult {
    sectorId: number;
    dryRun: boolean;
    candidates: number;
    evaluated: number;
    fired: number;
    skippedCooldown: number;
    errors: number;
    tokensInTotal: number;
    tokensOutTotal: number;
    durationMs: number;
    decisions: DispatchDecision[];
}

export interface FollowUpRuleStat {
    ruleId: string;
    name: string;
    sent: number;
    responded: number;
    responseRate: number;
}

export interface FollowUpStats {
    sectorId: number;
    days: number;
    sent: number;
    responded: number;
    responseRate: number;
    evaluated: number;
    fired: number;
    skipped: number;
    errors: number;
    lastRunAt: string | null;
    effectiveTimezone?: string;
    byRule: FollowUpRuleStat[];
}

export const getFollowUpStats = async (
    sectorId: number,
    days: number,
    token: string
): Promise<FollowUpStats> => {
    const response = await axios.get(`${API_URL}/FollowUp/${sectorId}/stats?days=${days}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.data?.data as FollowUpStats;
};

export const getFollowUpSettings = async (
    sectorId: number,
    token: string
): Promise<FollowUpSettingsDTO> => {
    const response = await axios.get(`${API_URL}/FollowUp/${sectorId}`, {
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.data?.data ?? { sectorId, enabled: false, rules: [] };
};

export const saveFollowUpSettings = async (
    sectorId: number,
    payload: SaveFollowUpRequest,
    token: string
): Promise<FollowUpSettingsDTO> => {
    const response = await axios.put(`${API_URL}/FollowUp/${sectorId}`, payload, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    return response.data?.data ?? { sectorId, enabled: payload.enabled, rules: payload.rules };
};

export const dispatchFollowUp = async (
    sectorId: number,
    dryRun: boolean,
    token: string
): Promise<DispatchResult> => {
    const response = await axios.post(
        `${API_URL}/FollowUp/${sectorId}/dispatch?dryRun=${dryRun ? 'true' : 'false'}`,
        {},
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            timeout: 5 * 60 * 1000,
        }
    );
    return response.data?.data as DispatchResult;
};

/** 06.3 — NL → draft estruturado. Nunca persiste nem ativa. */
export const suggestFollowUpRule = async (
    sectorId: number,
    text: string,
    token: string
): Promise<FollowUpRuleDTO> => {
    const response = await axios.post(
        `${API_URL}/FollowUp/${sectorId}/suggest-rule`,
        { text },
        {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        }
    );
    return response.data?.data as FollowUpRuleDTO;
};
